import { useCallback, useRef } from 'react';
import { useNavigate } from 'react-router';
import { MAT_STATUS_OPTIONS, MAT_FILTER_KEYS } from '../../../types/mat-types';
import { readUrlParam } from '../MATGrid.utils';

type GridFilterModel = Record<string, any>;

export function useMatUrlFilters() {
  const navigate = useNavigate();
  const applyingUrlFiltersRef = useRef(false);

  const setUrlFromFilterModel = useCallback(
    (model: GridFilterModel) => {
      // Read directly from browser URL to avoid stale React state
      // React's useLocation().search can lag one render behind after navigate()
      const currentSearch = window.location.search;
      const searchParams = new URLSearchParams(currentSearch);
      const next = new URLSearchParams(searchParams.toString());
      (MAT_FILTER_KEYS as readonly string[]).forEach((k) => next.delete(k));
      (MAT_FILTER_KEYS as readonly string[]).forEach((k) => {
        const m = (model as any)[k];
        if (!m) return;
        const val = (m?.filter as string) ?? (m?.value as string) ?? '';
        if (typeof val === 'string' && val.trim() !== '') next.set(k, val.trim());
      });
      next.set('page', '0');
      const curr = searchParams.toString();
      const nxt = next.toString();
      if (nxt !== curr) {
        navigate({ search: nxt }, { replace: false });
        return true;
      }
      return false;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [navigate]
  );

  const urlToFilterModel = useCallback(() => {
    // Read directly from browser URL (not useLocation) to avoid stale React state —
    // useLocation().search can lag one render behind after navigate()
    const searchParams = new URLSearchParams(window.location.search);
    const model: GridFilterModel = {};
    (MAT_FILTER_KEYS as readonly string[]).forEach((k) => {
      const v = readUrlParam(k, searchParams, MAT_STATUS_OPTIONS as readonly string[]);
      if (v != null && v !== '') {
        model[k] = { filterType: 'text', type: 'contains', filter: v };
      }
    });
    return model;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- called imperatively, not reactive
  }, []);

  const modelsEqual = useCallback((a: GridFilterModel, b: GridFilterModel) => {
    const norm = (m: GridFilterModel) => {
      const out: Record<string, string> = {};
      (MAT_FILTER_KEYS as readonly string[]).forEach((k) => {
        const cfg: any = (m as any)[k];
        if (!cfg) return;
        let v = '';
        v = String(cfg?.filter ?? '');
        if (v) out[k] = v;
      });
      return out;
    };
    const na = norm(a);
    const nb = norm(b);
    const ak = Object.keys(na).sort();
    const bk = Object.keys(nb).sort();
    if (ak.length !== bk.length) return false;
    for (let i = 0; i < ak.length; i++) {
      if (ak[i] !== bk[i]) return false;
      const k = ak[i];
      if (na[k] !== nb[k]) return false;
    }
    return true;
  }, []);

  return { applyingUrlFiltersRef, setUrlFromFilterModel, urlToFilterModel, modelsEqual };
}

export default useMatUrlFilters;
